import { markierTag } from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { heuteSchluessel } from '../../lib/markierung'
import { termineAmTagQuery } from '../../lib/queries'
import { AufgabenVerweis } from '../AufgabeKontext'

/**
 * **Der nächste Arbeitstag** – was My Work zeigt, wenn heute nichts mehr kommt (nach Arbeitsende,
 * am Wochenende). Nils, 23.10.2026, abends am Telefon: *„Da erkenne ich nichts"* – das Jetzt war
 * leer, „Als Nächstes" auch. Jetzt steht hier, was als Nächstes wirklich kommt.
 */
export function NaechsterTag({
  vorgenommen,
  abschliessen,
  schonAbgeschlossen,
  vorbereiten,
}: {
  /** Für den nächsten Werktag markiert (☀ Morgen). */
  vorgenommen: readonly { id: string; title: string }[]
  abschliessen: () => void
  schonAbgeschlossen: boolean
  vorbereiten: (t: { titel: string; beginn: string }) => void
}) {
  const tag = markierTag('morgen', heuteSchluessel())
  const { data, isLoading } = useQuery(termineAmTagQuery(tag))
  const termine = (data?.termine ?? [])
    .filter((t) => !t.abgesagt && !t.ganztaegig)
    .sort((a, b) => a.beginn.localeCompare(b.beginn))
  const name = new Date(`${tag}T12:00:00`).toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
  const uhr = (beginn: string) =>
    beginn.slice(0, 10) < tag
      ? `seit ${new Date(`${beginn.slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short' })}`
      : beginn.slice(11, 16)

  return (
    <section
      aria-label="Nächster Arbeitstag"
      className="flex flex-col gap-3 rounded-card border border-line bg-surface px-4 py-3.5 shadow-task"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-[15px] font-semibold">Als Nächstes: {name}</h2>
        <span className="text-[12.5px] text-ink2">
          {isLoading ? '…' : termine.length} {termine.length === 1 ? 'Termin' : 'Termine'} · ☀{' '}
          {vorgenommen.length} vorgenommen
        </span>
      </div>
      {isLoading ? (
        <p className="text-[13.5px] text-muted">Termine werden geladen …</p>
      ) : termine.length > 0 ? (
        <ul className="flex flex-col gap-1 text-[14px]">
          {termine.slice(0, 6).map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => vorbereiten({ titel: t.betreff, beginn: t.beginn })}
                className="flex min-h-10 w-full cursor-pointer items-center gap-3 rounded-lg text-left hover:bg-[#faf9f6]"
              >
                <span className="tabular w-[52px] shrink-0 text-ink2">{uhr(t.beginn)}</span>
                <span className="min-w-0 flex-1 truncate">{t.betreff}</span>
                <span aria-hidden="true" className="text-muted">
                  ›
                </span>
              </button>
            </li>
          ))}
          {termine.length > 6 && (
            <li className="text-[12.5px] text-muted">+ {termine.length - 6} weitere</li>
          )}
        </ul>
      ) : (
        <p className="text-[13.5px] text-ink2">Keine Termine – ein Tag für Tiefes.</p>
      )}
      {vorgenommen.length > 0 && (
        <ul className="flex flex-col gap-1 border-t border-line pt-2 text-[14px]">
          {vorgenommen.slice(0, 5).map((a) => (
            <li key={a.id} className="flex gap-2">
              <span className="text-sonne">☀</span>
              <AufgabenVerweis aufgabeId={a.id} className="min-w-0 truncate hover:text-accent-deep">
                {a.title}
              </AufgabenVerweis>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={abschliessen}
        className="min-h-11 cursor-pointer rounded-xl bg-[#4a3aa7] px-4 text-[14.5px] font-semibold text-white"
      >
        {schonAbgeschlossen ? 'Feierabend ✓ – noch einmal ansehen' : 'Tag abschließen'}
      </button>
    </section>
  )
}
